import supabase from "../config/supabase";

const notificationsApi = {
  async list({ limit = 50 } = {}) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) return { success: false, error: error.message };
    return { success: true, data: data || [] };
  },

  async get(notificationId) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { data, error } = await supabase
      .from("notifications")
      .select("*")
      .eq("id", notificationId)
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) return { success: false, error: error.message };
    if (!data) return { success: false, error: "Not found" };
    return { success: true, data };
  },

  async markRead(notificationId) {
    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("id", notificationId);
    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  async markAllRead() {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("user_id", user.id)
      .eq("is_read", false);

    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  async countUnread() {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { count, error } = await supabase
      .from("notifications")
      .select("*", { count: "exact", head: true })
      .eq("user_id", user.id)
      .eq("is_read", false);

    if (error) return { success: false, error: error.message };
    return { success: true, count: count || 0 };
  },

  /**
   * Confirm to a member, on their phone, that phone alerts now work.
   *
   * Written as an ordinary notifications row rather than a direct call to
   * send-push, because the row is what fires the trigger — so the confirmation
   * travels the exact path every real alert will take. If it lands on their
   * phone, the whole chain is proven: subscription saved, VAPID keys good,
   * trigger firing, service worker displaying. If it doesn't, they find out
   * now instead of wondering for nine days whether it's working, which is
   * roughly how long it took us.
   */
  async confirmPushEnabled(device = "phone") {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { error } = await supabase.from("notifications").insert({
      user_id: user.id,
      type: "push_enabled",
      title: "Phone alerts are on for Outback Exchange",
      // Goes to every device the member has alerts on for, so the body names
      // the one just set up and explains why this one is arriving elsewhere.
      message: `You just turned on alerts on your ${device}. This is what they'll look like. You'll get one when someone messages you or replies to your listing.`,
    });

    if (error) return { success: false, error: error.message };
    return { success: true };
  },

  /**
   * Notify all admins about a new pending item.
   * Creates or updates an aggregated notification (e.g., "5 new services awaiting approval").
   */
  async notifyAdmins(type, entityType, entityId) {
    console.log(`[notifyAdmins] Called with type=${type}, entityType=${entityType}, entityId=${entityId}`);
    
    // Get all admin users
    const { data: admins, error: adminError } = await supabase
      .from("users")
      .select("id")
      .eq("role", "admin");

    console.log(`[notifyAdmins] Found ${admins?.length || 0} admins`, admins);
    
    if (adminError || !admins?.length) {
      console.error(`[notifyAdmins] Error or no admins found:`, adminError);
      return { success: false, error: "No admins found" };
    }

    // Define notification titles and messages based on type
    const notificationConfig = {
      new_service_local: {
        title: "New Local Services",
        getMessageWithCount: (count) => `${count} new local service${count > 1 ? 's' : ''} awaiting approval`,
      },
      new_service_pro: {
        title: "New Pro Services",
        getMessageWithCount: (count) => `${count} new pro service${count > 1 ? 's' : ''} awaiting approval`,
      },
      new_job_ad: {
        title: "New Job Ads",
        getMessageWithCount: (count) => `${count} new job ad${count > 1 ? 's' : ''} awaiting approval`,
      },
    };

    const config = notificationConfig[type];
    if (!config) {
      console.error(`[notifyAdmins] Invalid notification type: ${type}`);
      return { success: false, error: "Invalid notification type" };
    }

    // For each admin, find or create an aggregated notification
    for (const admin of admins) {
      console.log(`[notifyAdmins] Processing admin: ${admin.id}`);
      
      // Check if there's an existing unread notification of this type
      const { data: existing, error: existingError } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", admin.id)
        .eq("type", type)
        .eq("is_read", false)
        .single();

      if (existingError && existingError.code !== 'PGRST116') {
        console.error(`[notifyAdmins] Error checking existing notification:`, existingError);
      }

      if (existing) {
        console.log(`[notifyAdmins] Found existing notification, updating count`);
        // Update existing: increment count in related_entity_id (we'll use this as a counter)
        const currentCount = parseInt(existing.related_entity_id) || 1;
        const newCount = currentCount + 1;

        const { error: updateError } = await supabase
          .from("notifications")
          .update({
            message: config.getMessageWithCount(newCount),
            related_entity_id: newCount.toString(),
            created_at: new Date().toISOString(), // Update timestamp to bubble to top
          })
          .eq("id", existing.id);
        
        if (updateError) {
          console.error(`[notifyAdmins] Error updating notification:`, updateError);
        } else {
          console.log(`[notifyAdmins] Updated notification count to ${newCount}`);
        }
      } else {
        console.log(`[notifyAdmins] Creating new notification`);
        // Create new notification
        const { error: insertError } = await supabase
          .from("notifications")
          .insert({
            user_id: admin.id,
            type: type,
            title: config.title,
            message: config.getMessageWithCount(1),
            is_read: false,
            related_entity_type: entityType,
            related_entity_id: "1", // Use as counter
          });
        
        if (insertError) {
          console.error(`[notifyAdmins] Error creating notification:`, insertError);
        } else {
          console.log(`[notifyAdmins] Created new notification for admin ${admin.id}`);
        }
      }
    }

    console.log(`[notifyAdmins] Completed successfully`);
    return { success: true };
  },
};

export default notificationsApi;
